import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

import "./dashboard.css";
import "../common/Util.css";
import Loader from "../common/Loader.jsx";
import { getForecastOverviewApi } from "./dashboard-api.js";

ChartJS.register(
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
);

const LINE_COLOR = "#6929E2";
const FORECAST_COLOR = "#A373FF";

const formatMoney = (value) =>
  `₹${(Number(value) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const formatShort = (value) => {
  const num = Number(value) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
  return formatMoney(num);
};

// Counts are whole things; a projected 4.6 leads is 5.
const formatValue = (value, unit) =>
  unit === "amount" ? formatShort(value) : Math.round(Number(value) || 0).toLocaleString("en-IN");

// "2026-11" reads as "Nov 26" in a tick label - the year matters because the
// chart spans one.
const monthLabel = (bucket) => {
  const [year, month] = String(bucket).split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return `${date.toLocaleString("en-US", { month: "short" })} ${String(year).slice(2)}`;
};

export const ForecastDashboard = () => {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState("lead");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const response = await getForecastOverviewApi();
        if (!live) return;
        if (response?.success === true) {
          setData(response.data);
        } else {
          setError(response?.message || "Could not load the forecast");
        }
      } catch (err) {
        if (live) setError("Could not load the forecast");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  const series = data?.series ?? [];
  const historyMonths = data?.history_months ?? [];
  const forecastMonths = data?.forecast_months ?? [];
  const pipeline = data?.pipeline ?? {};
  const selected = series.find((s) => s.key === active) ?? series[0];

  // History and forecast share one x axis. The forecast line is padded with
  // nulls so it starts where the actuals stop, and repeats the last actual
  // point so the two lines join instead of floating apart.
  const labels = [...historyMonths, ...forecastMonths].map(monthLabel);
  const trendChart = {
    labels,
    datasets: [
      {
        label: "Actual",
        data: [...(selected?.history ?? []), ...forecastMonths.map(() => null)],
        borderColor: LINE_COLOR,
        backgroundColor: "rgba(105, 41, 226, 0.08)",
        tension: 0.35,
        fill: true,
        pointRadius: 3,
      },
      {
        label: "Projected",
        data: [
          ...(selected?.history ?? []).map((v, i, arr) => (i === arr.length - 1 ? v : null)),
          ...(selected?.forecast ?? []),
        ],
        borderColor: FORECAST_COLOR,
        borderDash: [6, 4],
        tension: 0.35,
        pointRadius: 3,
      },
    ],
  };

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            `${ctx.dataset.label}: ${formatValue(ctx.parsed.y, selected?.unit)}`,
        },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: "#F1F2F4" },
        ticks: { callback: (v) => formatValue(v, selected?.unit) },
      },
    },
  };

  const pipelineChart = {
    labels: forecastMonths.map(monthLabel),
    datasets: [
      {
        label: "Open value",
        data: pipeline.amounts ?? [],
        backgroundColor: "#DECDFF",
        borderRadius: 6,
      },
      {
        label: "Weighted by probability",
        data: pipeline.weighted ?? [],
        backgroundColor: LINE_COLOR,
        borderRadius: 6,
      },
    ],
  };

  const pipelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8 } },
      tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatMoney(ctx.parsed.y)}` } },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, grid: { color: "#F1F2F4" }, ticks: { callback: formatShort } },
    },
  };

  return (
    <div className="w-100 p-3 h-100" style={{ backgroundColor: "#F7F8FC" }}>
      <div className="d-flex justify-content-between w-100">
        <div className="header-tabs">
          <button onClick={() => navigate("/dashboard")} className="dashboard-tab-btn">Leads Hub</button>
          <button onClick={() => navigate("/revenue")} className="dashboard-tab-btn">Revenue Overview</button>
          <button onClick={() => navigate("/pipeline")} className="dashboard-tab-btn">Pipeline</button>
          <button onClick={() => navigate("/forecast")} className="dashboard-tab-btn active">Forecast</button>
          <button onClick={() => navigate("/next-action")} className="dashboard-tab-btn">Next Actions</button>
        </div>
      </div>

      {loading ? (
        <Loader height="400px" />
      ) : error ? (
        <p className="text-danger fs-14 fw-400 text-center py-5 m-0">{error}</p>
      ) : (
        <>
          {/* One tile per module: what the next three months look like, and
              how that compares with the three just gone. Clicking one drives
              the chart below. */}
          <div className="row mt-3">
            {series.map((row) => {
              const up = (row.change_pct ?? 0) >= 0;
              return (
                <div key={row.key} className="col-xl col-md-4 col-6 mb-3 mb-xl-0">
                  <button
                    className={`bg-white rounded-4 p-3 d-flex flex-column gap-1 h-100 w-100 text-start ${
                      row.key === selected?.key ? "border border-2" : "border-0"
                    }`}
                    style={{
                      minHeight: "110px",
                      borderColor: row.key === selected?.key ? LINE_COLOR : undefined,
                    }}
                    onClick={() => setActive(row.key)}
                  >
                    <p className="textLightGrey fs-13 fw-400 m-0">{row.label}</p>
                    <p className="fs-20 fw-600 textDarkBlue1 m-0">
                      {formatValue(row.next_total, row.unit)}
                    </p>
                    <p
                      className="fs-12 fw-400 m-0"
                      style={{ color: row.change_pct === null ? "#7A869A" : up ? "#1F9254" : "#CC3333" }}
                    >
                      {row.change_pct === null
                        ? "no history to compare"
                        : `${up ? "▲" : "▼"} ${Math.abs(row.change_pct)}% vs last 3 months`}
                    </p>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="row mt-3">
            <div className="col-12">
              <div className="bg-white rounded-4 p-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-semibold m-0">{selected?.label} — 12 months actual, 3 projected</h5>
                  <span className="textLightGrey fs-12 fw-400">
                    Straight-line trend on monthly volume
                  </span>
                </div>
                <div style={{ height: "320px" }}>
                  <Line data={trendChart} options={trendOptions} />
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-3">
            <div className="col-lg-8 mb-3 mb-lg-0">
              <div className="bg-white rounded-4 p-3 h-100">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="fw-semibold m-0">Expected to close</h5>
                  <span className="textLightGrey fs-12 fw-400">
                    Open deals by expected close date
                  </span>
                </div>
                <div style={{ height: "280px" }}>
                  <Bar data={pipelineChart} options={pipelineOptions} />
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="bg-white rounded-4 p-3 h-100 d-flex flex-column gap-3">
                <div>
                  <p className="textLightGrey fs-13 fw-400 m-0">Weighted forecast, next 3 months</p>
                  <p className="fs-24 fw-700 textDarkBlue1 m-0">
                    {formatShort(pipeline.weighted_total)}
                  </p>
                </div>
                <div>
                  <p className="textLightGrey fs-13 fw-400 m-0">Deals in those months</p>
                  <p className="fs-20 fw-600 textDarkBlue1 m-0">
                    {(pipeline.counts ?? []).reduce((sum, n) => sum + Number(n || 0), 0)}
                  </p>
                </div>
                {/* Overdue and undated deals are excluded from the bars above,
                    so they are called out rather than quietly dropped. */}
                <div>
                  <p className="textLightGrey fs-13 fw-400 m-0">Overdue or undated</p>
                  <p className="fs-20 fw-600 textDarkBlue1 m-0">
                    {pipeline.unscheduled_count ?? 0}
                  </p>
                  <p className="textLightGrey fs-12 fw-400 m-0">
                    {formatShort(pipeline.unscheduled_amount)} not in the forecast
                  </p>
                </div>
                <button
                  className="bg-transparent rounded-pill px-3 py-2 fs-14 fw-600 textPurple mt-auto"
                  style={{ border: "1px solid #6929E2" }}
                  onClick={() => navigate("/opportunity/list")}
                >
                  Review open deals
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ForecastDashboard;
